import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useEditorRef, useEditorSelector } from 'platejs/react';
import { useMarkToolbarButton, useMarkToolbarButtonState } from '@platejs/utils/react';
import { toggleList, someList } from '@platejs/list';
import { BULLETED_STYLES, NUMBERED_STYLES } from '../listStyles';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatStrikethroughIcon from '@mui/icons-material/FormatStrikethrough';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import styles from './FloatingToolbar.module.scss';
import { computeFloatingToolbarPosition } from './floatingToolbarPosition';
import { FontSizeMenu } from '../FontSizeMenu/FontSizeMenu';
import { TextColorMenu } from '../TextColorMenu/TextColorMenu';
import { HighlightColorMenu } from '../HighlightColorMenu/HighlightColorMenu';
import { CellBackgroundMenu } from '../CellBackgroundMenu/CellBackgroundMenu';
import { AlignMenu } from '../AlignMenu/AlignMenu';
import { AIToolbarButton } from '../AIToolbarButton/AIToolbarButton';
import { useFeatureVisibility } from '../../../../utils/useVisibilityConfig';
import { VISIBILITY_KEYS } from '../../../../utils/constants/visibilityKeys';
import { WHATSAPP_TOOLBAR_VARIANT, type ToolbarVariant } from '../../types/types';
import { countRender } from '../../debug/platePerf';

// Compact toolbar that appears just above any non-empty text selection
// inside the editor. Light theme so reused menus (font size, colors)
// match the main PlateToolbar look. Selection tracking via
// `selectionchange` + containment check against
// `[data-testid="plate-editor-content"]`. Rendered into <body> via portal
// so it can escape any overflow clipping on the editor wrapper.

interface MarkButtonProps {
    nodeType: string;
    label: string;
    children: React.ReactNode;
}

function MarkButton({ nodeType, label, children }: MarkButtonProps) {
    const state = (useMarkToolbarButtonState as any)({ nodeType });
    const { props: btnProps } = (useMarkToolbarButton as any)(state);
    return (
        <button
            type="button"
            {...btnProps}
            className={clsx(styles.btn, state.pressed && styles.btnActive)}
            title={label}
            aria-label={label}
            aria-pressed={state.pressed || undefined}
        >
            {children}
        </button>
    );
}

interface ListButtonProps {
    listStyleType: string;
    active: boolean;
    label: string;
    children: React.ReactNode;
}

function ListButton({ listStyleType, active, label, children }: ListButtonProps) {
    const editor = useEditorRef();
    const onMouseDown = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            (toggleList as any)(editor, { listStyleType });
        },
        [editor, listStyleType],
    );
    return (
        <button
            type="button"
            className={clsx(styles.btn, active && styles.btnActive)}
            onMouseDown={onMouseDown}
            title={label}
            aria-label={label}
            aria-pressed={active || undefined}
        >
            {children}
        </button>
    );
}

export function FloatingToolbar({ variant = 'full' }: { variant?: ToolbarVariant }) {
    countRender('floating-toolbar');
    const isFull = variant === 'full';
    // The floating bubble must obey the same five-control limit as the docked toolbar —
    // otherwise selecting text hands back exactly the formatting the variant took away,
    // and plateToWhatsApp would silently drop it on send.
    const isWhatsApp = variant === WHATSAPP_TOOLBAR_VARIANT;
    // `pos` is the auto-position derived from the selection — it also gates
    // visibility (null = hidden). `manualPos` is an optional drag override:
    // once the user drags the toolbar by its grip, it sticks at that spot until
    // the selection collapses (which clears it, so the next selection pops fresh
    // above the text again).
    const [pos, setPos] = useState<{ left: number; top: number; bottom: number } | null>(null);
    const [manualPos, setManualPos] = useState<{ left: number; top: number } | null>(null);
    const [dragging, setDragging] = useState(false);
    const dragOriginRef = useRef<{ px: number; py: number; left: number; top: number } | null>(null);
    // Mirrors `dragging` for the selectionchange/scroll listener (its effect has
    // empty deps and can't see fresh state) so we can freeze repositioning while
    // the user is dragging the bubble.
    const draggingRef = useRef(false);

    const isBulletedList = useEditorSelector((ed: any) => (someList as any)(ed, BULLETED_STYLES), []);
    const isNumberedList = useEditorSelector((ed: any) => (someList as any)(ed, NUMBERED_STYLES), []);

    // Gate the AI group + its divider on `editor.AI.enabled`. Same belt-and-
    // suspenders pattern as PlateToolbar — the button null-returns when off,
    // but gating here avoids the empty `.group` div + dangling divider on
    // the left edge of the floating toolbar.
    const { aiEnabled } = useFeatureVisibility({ aiEnabled: VISIBILITY_KEYS.AI.ENABLED });

    useEffect(() => {
        let raf = 0;
        // Cheap pre-check that short-circuits before scheduling a RAF on
        // every keystroke. `selectionchange` fires while the caret moves
        // during typing — the toolbar only ever shows on a non-collapsed
        // selection, so we can bail synchronously when collapsed.
        const isCollapsedNow = () => {
            const sel = document.getSelection();
            return !sel || sel.rangeCount === 0 || sel.isCollapsed;
        };
        const update = () => {
            // While dragging, the manual position owns the bubble; recomputing
            // from the (still-live) selection here would only churn renders.
            if (draggingRef.current) return;
            if (isCollapsedNow()) {
                cancelAnimationFrame(raf);
                setPos(null);
                return;
            }
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => {
                const editorEl = document.querySelector('[data-testid="plate-editor-content"]') as HTMLElement | null;
                if (!editorEl) {
                    setPos(null);
                    return;
                }
                // Keep the toolbar visible while a popover (font size,
                // color pickers) is open — those mount as DOM children of
                // the toolbar, so `activeElement` may move into them.
                const active = document.activeElement;
                const toolbarEl = document.querySelector('[data-testid="plate-floating-toolbar"]');
                const focusInsideEditor = active && editorEl.contains(active);
                const focusInsideToolbar = active && toolbarEl && toolbarEl.contains(active);
                if (!focusInsideEditor && !focusInsideToolbar) {
                    setPos(null);
                    return;
                }
                const sel = document.getSelection();
                if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
                    setPos(null);
                    return;
                }
                const range = sel.getRangeAt(0);
                if (!editorEl.contains(range.commonAncestorContainer)) {
                    setPos(null);
                    return;
                }
                const rect = range.getBoundingClientRect();
                if (!rect || rect.width < 2 || rect.height < 2) {
                    setPos(null);
                    return;
                }
                const left = rect.left + rect.width / 2;
                const top = rect.top;
                const bottom = rect.bottom;
                // Equality-bail: `selectionchange` + scroll fire constantly; if
                // the measured rect is unchanged, keep the same object so React
                // skips the re-render (a fresh object literal never would).
                setPos((prev) => (prev && prev.left === left && prev.top === top && prev.bottom === bottom ? prev : { left, top, bottom }));
            });
        };
        // Name every listener so removeEventListener gets the same
        // reference. Anonymous arrows leak — every editor mount used to
        // accumulate stale listeners on document/window.
        const hide = () => setPos(null);
        const onFocusOut = () => requestAnimationFrame(update);
        document.addEventListener('selectionchange', update);
        document.addEventListener('focusin', update);
        document.addEventListener('focusout', onFocusOut);
        window.addEventListener('blur', hide);
        window.addEventListener('scroll', update, true);
        window.addEventListener('resize', update);
        return () => {
            cancelAnimationFrame(raf);
            document.removeEventListener('selectionchange', update);
            document.removeEventListener('focusin', update);
            document.removeEventListener('focusout', onFocusOut);
            window.removeEventListener('blur', hide);
            // The capture flag MUST match — addEventListener(_, _, true) and
            // removeEventListener(_, _) target different listener slots and
            // the latter is a silent no-op.
            window.removeEventListener('scroll', update, true);
            window.removeEventListener('resize', update);
        };
    }, []);

    // Clear any drag override when the toolbar hides (selection collapsed) so a
    // new selection always pops the toolbar fresh above the text.
    useEffect(() => {
        if (!pos) setManualPos(null);
    }, [pos]);

    // Drag-to-move via the grip. Window listeners track the pointer so the move
    // continues even if the cursor outruns the toolbar.
    useEffect(() => {
        if (!dragging) return undefined;
        const onMove = (e: PointerEvent) => {
            const o = dragOriginRef.current;
            if (!o) return;
            setManualPos({ left: o.left + (e.clientX - o.px), top: o.top + (e.clientY - o.py) });
        };
        const onUp = () => {
            draggingRef.current = false;
            setDragging(false);
            dragOriginRef.current = null;
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        return () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
        };
    }, [dragging]);

    const startDrag = useCallback(
        (e: ReactPointerEvent) => {
            const base = manualPos ?? pos;
            if (!base) return;
            e.preventDefault();
            e.stopPropagation();
            dragOriginRef.current = { px: e.clientX, py: e.clientY, left: base.left, top: base.top };
            draggingRef.current = true;
            setDragging(true);
        },
        [manualPos, pos],
    );

    if (!pos || typeof document === 'undefined') return null;
    // A manual drag positions the bubble freely (kept above the cursor as
    // before); otherwise auto-place it from the selection — flipping below and
    // clamping to the viewport so it can never render off-screen.
    const placed = manualPos
        ? { left: manualPos.left, top: manualPos.top, placement: 'above' as const }
        : computeFloatingToolbarPosition(
              { top: pos.top, bottom: pos.bottom, centerX: pos.left },
              { width: window.innerWidth, height: window.innerHeight },
          );
    return createPortal(
        <div
            role="toolbar"
            aria-label="Inline formatting"
            data-testid="plate-floating-toolbar"
            className={clsx(styles.floating, placed.placement === 'below' ? styles.below : styles.above, dragging && styles.dragging)}
            style={{ left: placed.left, top: placed.top }}
            // Stop mousedown from collapsing the selection (the toolbar buttons
            // need the selection alive to apply marks to it).
            onMouseDown={(e) => e.preventDefault()}
        >
            {/* AI assistant — leftmost slot, only renders when `editor.AI.enabled`
                is on. Icon-only here: the "Ask Aica" text label widens the
                button past the compact bubble's chrome and overflows it, so the
                label lives only in the main PlateToolbar. Passing the host's
                `btn` class keeps it a 28px square matching the other buttons. */}
            {aiEnabled.write && !isWhatsApp && (
                <>
                    <div className={styles.group}>
                        <AIToolbarButton className={styles.btn} iconOnly />
                    </div>
                    <div className={styles.divider} aria-hidden />
                </>
            )}

            {isFull && (
                <>
                    <div className={styles.group}>
                        <FontSizeMenu />
                    </div>
                    <div className={styles.divider} aria-hidden />
                </>
            )}

            <div className={styles.group}>
                <MarkButton nodeType="bold" label="Bold (⌘/Ctrl+B)">
                    <FormatBoldIcon fontSize="small" />
                </MarkButton>
                <MarkButton nodeType="italic" label="Italic (⌘/Ctrl+I)">
                    <FormatItalicIcon fontSize="small" />
                </MarkButton>
                {!isWhatsApp && (
                    <MarkButton nodeType="underline" label="Underline (⌘/Ctrl+U)">
                        <FormatUnderlinedIcon fontSize="small" />
                    </MarkButton>
                )}
                <MarkButton nodeType="strikethrough" label="Strikethrough">
                    <FormatStrikethroughIcon fontSize="small" />
                </MarkButton>
            </div>
            {!isWhatsApp && <div className={styles.divider} aria-hidden />}

            {!isWhatsApp && (
                <div className={styles.group}>
                    <TextColorMenu />
                    <HighlightColorMenu />
                    {/* Self-disables when the selection isn't inside a <td>/<th>;
                    no outer gating needed. Mirrors PlateToolbar's ordering
                    (text color → highlight → cell fill). */}
                    <CellBackgroundMenu />
                </div>
            )}

            {isFull && (
                <>
                    <div className={styles.divider} aria-hidden />
                    <div className={styles.group}>
                        <AlignMenu />
                    </div>
                </>
            )}

            {(isFull || isWhatsApp) && (
                <>
                    <div className={styles.divider} aria-hidden />
                    <div className={styles.group}>
                        <ListButton listStyleType="disc" active={isBulletedList} label="Bulleted list">
                            <FormatListBulletedIcon fontSize="small" />
                        </ListButton>
                        <ListButton listStyleType="decimal" active={isNumberedList} label="Numbered list">
                            <FormatListNumberedIcon fontSize="small" />
                        </ListButton>
                    </div>
                </>
            )}

            {/* Drag grip — rightmost slot. Lets the user pull the toolbar off the
                selection when it covers the text they're editing. */}
            <div className={styles.divider} aria-hidden />
            <button type="button" className={styles.dragHandle} onPointerDown={startDrag} title="Drag to move" aria-label="Drag toolbar">
                <DragIndicatorIcon fontSize="small" />
            </button>
        </div>,
        document.body,
    );
}
